/**
 * useMe - React Query hook for the authenticated user's profile + live
 * subscription snapshot (GET /me, exempt from the subscription gate so a
 * cancelled/suspended user can still read it to reach billing).
 *
 * The subscription status drives the app-shell gate:
 *   past_due            → warning banner (access still allowed)
 *   cancelled/suspended → 402 interstitial (clinical access blocked server-side)
 *   pilot/trialing/active → no gate UI
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getMe, type MePayload, type MeResponse } from '../api/me.ts';
import { useTokenStore } from '../api/use-api.ts';
import { useAuth } from './auth-context.tsx';

/**
 * The raw payload, narrow or full. Only the gate chain (ProtectedLayout) and the
 * screens that run inside the gate may read it.
 */
export function useMeGate(): UseQueryResult<MePayload> {
  const store = useTokenStore();
  const { accessToken, bootstrapped } = useAuth();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => getMe(store),
    // Do not fire until the on-load silent refresh has resolved and a token is
    // present - otherwise a tokenless GET /me would launch a second concurrent
    // refresh that races the bootstrap on the same rotating cookie.
    enabled: bootstrapped && !!accessToken,
    staleTime: 30_000,
  });
}

/**
 * `data` is undefined while the gate holds the session, so a post-MFA consumer
 * can never read a field the narrow payload does not carry. ProtectedLayout has
 * already redirected by then; the undefined is what the render race sees.
 */
export function useMe(): UseQueryResult<MeResponse> {
  const q = useMeGate();
  return (
    q.data?.mfaGate === 'pending' ? { ...q, data: undefined } : q
  ) as UseQueryResult<MeResponse>;
}
